// Catálogo cerrado de los textos fijos de las piezas (diseño 4a, §2.0 y §6; registrado en AGENTS.md, «Piezas visuales»).
// Las piezas no redactan: todo texto del dibujo sale del documento argus.hallazgos/1 o de aquí (prueba P9). Cada texto es el aprobado
// en el diseño §6 (los que se aprueban sobre capturas de dev:mock van igual que en §6); todos pasan el glosario (P11).
// Los huecos entre llaves los llena la pieza: {n} número, {anio} año, {fecha} fecha «10 sep 2024», {nombre} y {lista} texto del documento.
import type { EstadoLista, Identidad } from '../hallazgos/tipos.ts';

export const ROTULOS = {
  // 15. Leyenda.
  leyendaDocumento: 'Lo dice un documento',
  leyendaArgus: 'Lo deduce Argus',
  leyendaNoSabemos: 'No sabemos',
  // 17, 17b, 17c. Lo que no cupo.
  masRelaciones: '+{n} relaciones más',
  masRelacion: '+1 relación más',
  omitidosHallazgos: '{n} relaciones no caben en la ficha; están en el detalle.',
  omitidoHallazgo: '1 relación no cabe en la ficha; está en el detalle.',
  omitidosLejanas: '{n} relaciones lejanas, en el detalle.',
  omitidaLejana: '1 relación lejana, en el detalle.',
  // 18. Rótulo de la flecha («Socio · 12 % · acta 2016», decisión 2 de Bernardo).
  acta: 'acta {anio}',
  porcentaje: '{n} %',
  accionesSinTotal: '{n} acciones, no sabemos el total',
  // 19 a 22. Línea de tiempo.
  tituloTiempo: '¿Antes o después de la sanción?',
  subtituloTiempo: 'Fechas de las actas y de la lista de OFAC',
  ultimaActa: 'Última acta ({anio})',
  sinActas: 'sin actas: no sabemos',
  sanciona: 'OFAC sanciona a {nombre} · {fecha}',
  anio: '{anio}',
  // 23 a 27. Lo que dice el Tesoro.
  porQue: 'Por qué OFAC sancionó a {nombre}',
  significa: 'Qué significa la sanción:',
  traduccion: 'Traducción de Argus',
  leerOriginal: 'Leer el original en inglés',
  // 5 a 7e y 57, 58. Comparación con el cliente.
  coincidenDe: 'Coinciden {n} de {m} datos: {lista}.',
  coincideUnoDe: 'Coincide 1 de {m} datos: {lista}.',
  coincideUnoDeUno: 'Coincide 1 de 1 dato: {lista}.',
  coincidenDeCorto: 'Coinciden {n} de {m} datos',
  coincidenDeUnoCorto: 'Coinciden {n} de 1 dato',
  coincideUnoDeCorto: 'Coincide 1 de {m} datos',
  coincideUnoDeUnoCorto: 'Coincide 1 de 1 dato',
  // 57. Marcas de cada dato en el buscador y en la ficha (sólo en HTML, nunca en un dibujo). {dato}: el rótulo de CAMPO.
  marcaCoincide: '✓ {dato}',
  marcaNoCoincide: '✗ {dato}',
  marcaFalta: '— {dato}: falta',
  coincideEnParte: 'Coincide sólo en parte: {lista}.',
  falta: 'Falta {lista}.',
  noCoincide: 'No coincide: {lista}.',
  noSePuedeComparar: 'No se puede comparar: {lista}.',
  coincidenCiudadEstado: 'Coinciden la ciudad y el estado.',
  soloAnio: 'Sólo tenemos el año: no se puede comparar.',
  unaActaSiOtraNo: 'Una de sus actas coincide y otra no: {lista}.',
  homoclaveDistinta: 'Coincide la raíz del RFC; la homoclave es distinta.',
  clienteNoLoDio: 'Tu cliente no lo dio.',
  argusNoTienePersona: 'Argus no tiene este dato de la persona encontrada.',
  argusNoTieneEmpresa: 'Argus no tiene este dato de la empresa encontrada.',
  // 53b. La fuente no cargó.
  sinDibujo: 'No pudimos mostrar el dibujo; abajo está lo mismo en texto.',
} as const;

/** Sello por estado en la lista (16). `null`: sin sello. */
export const SELLO_LISTA: Record<EstadoLista['estado'], string | null> = {
  en_lista: 'En la lista de OFAC',
  cuenta_como_sancionada: 'Cuenta como sancionada',
  retirada: 'Salió de la lista de OFAC',
  posible: 'Identidad por confirmar',
  no_esta: null,
};

/** Sello por identidad de una contraparte (16). `null`: sin sello de identidad (vale el de la lista). */
export const SELLO_IDENTIDAD: Record<Identidad['nivel'], string | null> = {
  confirmada: null,
  posible: 'Identidad por confirmar',
  no_sabemos: 'Identidad por confirmar',
  // 2026-10-05: con su fuente, y sin «persona» (también se usa con empresas)
  descartada: 'No es la de la lista de OFAC',
};

/** Nombre de cada dato en las listas de la comparación (6, 7 y 5b). */
export const DATO = {
  fecha_persona: 'la fecha de nacimiento',
  fecha_empresa: 'la fecha de constitución',
  domicilio: 'el domicilio',
  lugar_nacimiento: 'el lugar de nacimiento',
  rfc: 'el RFC',
  curp: 'la CURP',
  anio_persona: 'el año de nacimiento',
  anio_empresa: 'el año de constitución',
  mes_persona: 'el mes de nacimiento',
  mes_empresa: 'el mes de constitución',
  raiz_rfc: 'la raíz del RFC, sin homoclave',
  raiz_rfc_homoclave: 'la raíz del RFC',
} as const;

/** Rótulo de cada dato en la tabla de la comparación y en las marcas del buscador (54, 57). */
export const CAMPO = {
  fecha_persona: 'Fecha de nacimiento',
  fecha_empresa: 'Fecha de constitución',
  domicilio: 'Domicilio',
  lugar_nacimiento: 'Lugar de nacimiento',
  rfc: 'RFC',
  curp: 'CURP',
} as const;

/** Llena un rótulo. */
export function rotulo(plantilla: string, valores: Record<string, string | number>): string {
  return plantilla.replace(/\{(\w+)\}/g, (_, k: string) => {
    if (!(k in valores)) throw new Error(`rótulo sin valor para {${k}}: ${plantilla}`);
    return String(valores[k]);
  });
}

/** Lista en español: «a», «a y b», «a, b y c». */
export function enLista(xs: readonly string[]): string {
  return xs.length <= 1 ? (xs[0] ?? '') : `${xs.slice(0, -1).join(', ')} y ${xs.at(-1)}`;
}

/** Todos los textos fijos del catálogo, para las pruebas (glosario y origen). */
export function textosDelCatalogo(): string[] {
  return [
    ...Object.values(ROTULOS),
    ...Object.values(SELLO_LISTA).filter((x): x is string => x !== null),
    ...Object.values(SELLO_IDENTIDAD).filter((x): x is string => x !== null),
    ...Object.values(DATO),
    ...Object.values(CAMPO),
  ];
}

const MESES = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];

/** «2024-09-10» → «10 sep 2024»; «2024-09» → «sep 2024»; «2024» → «2024» (como las frases de la base). Nunca rellena. */
export function fechaLlana(f: string): string {
  const [a, m, d] = f.split('-');
  if (!m) return a!;
  const mes = MESES[Number(m) - 1] ?? m;
  return d ? `${Number(d)} ${mes} ${a}` : `${mes} ${a}`;
}

/**
 * Número con el formato de la base (`nexo.siger_numero`): miles con coma y hasta dos decimales sin ceros sobrantes (49.96 → «49.96»,
 * 33.333 → «33.33», 1000 → «1,000»); algo positivo menor que 0.005 es «menos de 0.01», nunca «0». Redondea a dos decimales como
 * `to_char` (la mitad se aleja del cero).
 */
export function numeroLlano(n: number): string {
  if (n > 0 && n < 0.005) return 'menos de 0.01';
  const centesimos = Math.round(Math.abs(Number((n * 100).toPrecision(15))));
  const ent = String(Math.floor(centesimos / 100)).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  const dec = String(centesimos % 100).padStart(2, '0').replace(/0+$/, '');
  return `${n < 0 && centesimos > 0 ? '-' : ''}${ent}${dec ? `.${dec}` : ''}`;
}
